<template>
  <section
    id="pricing"
    class="growth-section growth-plans"
  >
    <div class="growth-section-heading">
      <p class="growth-eyebrow">
        {{ $t('refresh.plans') }}
      </p><h2>{{ $t('refresh.plans-title') }}</h2><p>{{ $t('refresh.plans-description') }}</p>
    </div>
    <div
      class="growth-plans__toggle"
      :aria-label="$t('refresh.billing')"
      role="group"
    >
      <button
        :aria-pressed="!isYearly"
        @click="isYearly = false"
      >
        {{ $t('settings.monthly') }}
      </button><button
        :aria-pressed="isYearly"
        @click="isYearly = true"
      >
        {{ $t('settings.yearly') }}
      </button>
    </div>
    <div class="growth-plan-grid">
      <article class="growth-plan">
        <div class="growth-plan__title">
          <h3>{{ $t('refresh.free') }}</h3>
        </div>
        <div class="growth-plan__price">
          <strong>$0</strong><span>USD / {{ $t('general.year') }}</span>
        </div>
        <p class="growth-plan__description">
          {{ $t('refresh.free-description') }}
        </p>
        <ul><li><GrowthIcon name="check" />1 {{ $t('navigation.artists') }}</li><li><GrowthIcon name="check" />1 {{ $t('releases.release') }} / {{ $t('general.month') }}</li><li><GrowthIcon name="check" />{{ $t('refresh.free-feature') }}</li></ul>
        <details><summary>{{ $t('refresh.plan-details') }}</summary><p>85% {{ $t('royalties.royalty-split') }} · {{ $t('royalties.min-payment') }}: $100 USD</p></details>
        <router-link
          to="/auth/signup"
          class="growth-button"
        >
          {{ $t('refresh.start-free') }}<GrowthIcon name="up" />
        </router-link>
      </article>
      <article class="growth-plan growth-plan--pro">
        <div class="growth-plan__title">
          <h3>PRO</h3><span class="refresh-pro-tag">{{ $t('refresh.for-your-next-chapter') }}</span>
        </div>
        <div class="growth-plan__price">
          <strong>${{ isYearly ? '49' : '9.99' }}</strong><span>USD / {{ isYearly ? $t('general.year') : $t('general.month') }}</span>
        </div>
        <p class="growth-plan__description">
          {{ $t('refresh.pro-plan-description') }}
        </p>
        <ul>
          <li
            v-for="feature in proFeatures"
            :key="feature"
          >
            <GrowthIcon name="check" />{{ $t('settings.' + feature) }}
          </li>
        </ul>
        <details>
          <summary>{{ $t('refresh.plan-details') }}</summary><p>5 {{ $t('navigation.artists') }} · 25 {{ $t('navigation.releases') }} / {{ $t('general.month') }}<br>100% {{ $t('royalties.royalty-split') }}* · {{ $t('royalties.min-payment') }}: $50 USD<br>{{ $t('settings.custom-label') }} · {{ $t('settings.content-id') }}</p><router-link
            to="/terms-and-conditions"
            class="growth-text-link"
          >
            {{ $t('refresh.terms') }}<GrowthIcon name="up" />
          </router-link>
        </details>
        <button
          class="growth-button growth-button--dark"
          :disabled="isLoading"
          @click="upgradePlan('pro')"
        >
          {{ isLoading ? $t('general.loading') : $t('refresh.choose-pro') }}<GrowthIcon name="up" />
        </button>
      </article>
      <article class="growth-plan">
        <div class="growth-plan__title">
          <h3>{{ $t('refresh.advanced') }}</h3>
        </div>
        <div class="growth-plan__price">
          <strong>$199</strong><span>USD / {{ $t('general.year') }}</span>
        </div>
        <p class="growth-plan__description">
          {{ $t('refresh.advanced-description') }}
        </p>
        <ul><li><GrowthIcon name="check" />{{ $t('settings.pro-features') }}</li><li><GrowthIcon name="check" />50 {{ $t('navigation.artists') }}</li><li><GrowthIcon name="check" />100 {{ $t('navigation.releases') }} / {{ $t('general.month') }}</li></ul>
        <details>
          <summary>{{ $t('refresh.plan-details') }}</summary><p>{{ $t('settings.yearly-only') }}</p><router-link
            to="/terms-and-conditions"
            class="growth-text-link"
          >
            {{ $t('refresh.terms') }}<GrowthIcon name="up" />
          </router-link>
        </details>
        <button
          class="growth-button"
          :disabled="isLoading"
          @click="upgradePlan('advanced')"
        >
          {{ isLoading ? $t('general.loading') : $t('refresh.choose-advanced') }}<GrowthIcon name="up" />
        </button>
      </article>
    </div>
    <p class="growth-plan__note">
      {{ isYearly ? $t('refresh.annual-note') : $t('settings.billed-monthly') }}
    </p>
  </section>
</template>
<script setup>
import { RouterLink } from 'vue-router';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@vueuse/head';
import { useAuthStore } from '@/store/auth';
import userService from '@/services/userService';
import { SITE_URL, SITE_NAME } from '@/config/seo';
import { acquisitionPayload, isPaidAcquisition } from '@/utils/acquisition';

const stripe = ref(null);
const isLoading = ref(false);
const isYearly = ref(true);
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();


const { t, locale } = useI18n();

const offersJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: `${SITE_NAME} Music Distribution`,
  description: t('landing.page_description'),
  brand: {
    '@type': 'Brand',
    name: SITE_NAME,
  },
  offers: [
    {
      '@type': 'Offer',
      name: 'Free',
      description: t('settings.plan-descriptions.free'),
      price: '0',
      priceCurrency: 'USD',
      url: `${SITE_URL}/auth/signup`,
      availability: 'https://schema.org/InStock',
      category: 'Free',
    },
    {
      '@type': 'Offer',
      name: 'PRO',
      description: t('settings.plan-descriptions.pro'),
      price: isYearly.value ? '49' : '9.99',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: isYearly.value ? '49' : '9.99',
        priceCurrency: 'USD',
        billingDuration: isYearly.value ? 'P1Y' : 'P1M',
        unitText: isYearly.value ? 'YEAR' : 'MONTH',
      },
      url: `${SITE_URL}/upgrade`,
      availability: 'https://schema.org/InStock',
      category: 'Subscription',
    },
    {
      '@type': 'Offer',
      name: 'Advanced',
      description: t('settings.plan-descriptions.advanced'),
      price: '199',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '199',
        priceCurrency: 'USD',
        billingDuration: 'P1Y',
        unitText: 'YEAR',
      },
      url: `${SITE_URL}/upgrade`,
      availability: 'https://schema.org/InStock',
      category: 'Subscription',
    },
  ],
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      key: 'pricing-product-jsonld',
      children: computed(() => JSON.stringify(offersJsonLd.value)),
    },
  ],
});

const initializeStripe = () => {
  let stripeKey = 'pk_live_Fy0LR9SOyRoSrq4Byoj3mQtS00GjNUCMRm';

  if (window.location.href.match(/localhost|cms-development/)) {
    stripeKey = 'pk_test_WD9FBh5jNgV5uO1eFQBkLdAl000Ey6vyno';
  }

  if (typeof window.Stripe === 'function') stripe.value = window.Stripe(stripeKey);
};

const upgradePlan = async (plan) => {
  if (isLoading.value) return;
  const acquisition = acquisitionPayload();
  if (!authStore.currentUser && isPaidAcquisition(acquisition)) {
    await router.push({ path: '/auth/signup', query: { lang: locale.value, redirect: router.resolve({ path: '/upgrade/', query: { ...route.query, intent: 'distribution', plan, billing: plan === 'advanced' || isYearly.value ? 'yearly' : 'monthly' } }).fullPath } });
    return;
  }
  if (!stripe.value) initializeStripe();
  if (!stripe.value) {
    alert(t('settings.stripe-not-loaded'));
    return;
  }

  isLoading.value = true;

  try {
    let priceId;
    if (plan === 'pro') {
      priceId = isYearly.value ? 'price_1LFOeXK9qKRAJ5zqfGt7fcx8' : 'price_1HccB9K9qKRAJ5zqKJCo4gf0';
    } else {
      priceId = 'plan_GxucdZQjJLKPiZ'; // Advanced is always yearly
    }

    const subscriptionData = {
      from: route.query.from || 'landing',
      ...acquisition,
    };

    window.gtag?.('event', 'begin_checkout', { currency: 'USD', value: plan === 'pro' ? (isYearly.value ? 49 : 9.99) : 199 });

    await userService.setSubscriptionTrackingData(subscriptionData).catch(() => {});
    {
      const result = await stripe.value.redirectToCheckout({
        lineItems: [{ price: priceId, quantity: 1 }],
        mode: 'subscription',
        customerEmail: authStore.currentUser?.email,
        successUrl: 'https://indiefy.net/v1/api/user/upgrading?session_id={CHECKOUT_SESSION_ID}&beta=1',
        cancelUrl: 'https://indiefy.net/upgrade/?cancelled=1',
      });

      if (result.error) {
        alert(result.error.message);
      }
    }
  } catch (error) {
    console.error('Stripe checkout error:', error);
    alert(t('settings.upgrade-error'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  initializeStripe();
});

import GrowthIcon from '@/components/growth/GrowthIcon.vue';
const proFeatures = ['presave', 'playlist-submission', 'premium-stores', 'faster-releases', 'dedicated-support'];
</script>
